import React from 'react'
import styled, { css } from 'styled-components'
import { bool } from 'prop-types'
import { rgba, stripUnit } from 'polished'
import { Link } from 'gatsby'

import { white, black, greyLight } from 'styles/colors'
import { media, mediaDown } from 'styles/media'
import { fontSizes } from 'styles/typography'
import Wrapper from 'components/Wrapper'
import Section from 'components/Section'
import * as spacing from 'styles/spacing'
import SocialLink from './SocialLink'
import FooterLink from './FooterLink'
import Logo from 'images/logo.svg'
import StackedLogo from 'images/logo-stacked.svg'

const Container = styled.div`
  background-color: #111214;
  color: ${white};

  ${props =>
    props.isLight &&
    css`
      color: ${black};
      background-color: ${white};
      border-top: 1px solid ${greyLight};
    `}

  ${mediaDown.medium`
    text-align: center;
  `};
`

const Main = styled.div`
  border-bottom: 1px solid ${rgba(white, 0.15)};

  ${props =>
    props.isLight &&
    css`
      border-color: ${greyLight};
    `}
`

const Inner = styled.div`
  ${media.medium`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
  `};
`

const Links = styled.div`
  display: flex;
  flex-direction: column-reverse;

  ${media.medium`
    flex-direction: row-reverse;
    justify-content: space-between;
    margin-left: auto;
    align-items: flex-start;
  `};
`

const List = styled.div`
  display: flex;

  ${mediaDown.medium`
    justify-content: center;
    margin-top: ${spacing.xLarge};
  `};

  ${media.medium`
    flex-direction: column;
    width: 240px;
    padding-left: ${spacing.large};
  `};

  ${media.large`
    width: 290px;
    padding-left: ${spacing.xLarge};
  `};
`

const Block = styled.div`
  position: static;

  & + & {
    margin-top: ${spacing.large};

    ${media.medium`
      margin-top: ${spacing.xLarge};
    `};

    ${media.large`
      margin-top: ${spacing.xxLarge};
    `};
  }
`

const Disclaimer = styled.p`
  color: ${rgba(white, 0.75)};
  font-size: ${fontSizes.tiny};

  ${props =>
    props.isLight &&
    css`
      color: ${black};
    `}

  ${media.medium`
    text-align: center;
  `};

  ${media.xLarge`
    margin-left: -${spacing.medium};
    margin-right: -${spacing.medium};
  `};
`

const MobileLogo = styled(StackedLogo)`
  fill: ${white};
  width: 62px;
  height: 51px;

  ${props =>
    props.isLight &&
    css`
      fill: ${black};
    `}
`

const DesktopLogo = styled(Logo)`
  fill: ${white};
  width: 114px;
  height: 34px;

  ${props =>
    props.isLight &&
    css`
      fill: ${black};
    `}

  ${media.xLarge`
    width: 124px;
    height: 37px;
  `};
`

const SecondaryList = styled.div`
  position: static;

  ${media.medium`
    display: flex;
    justify-content: center;
  `};
`

const SecondaryLink = styled(Link)`
  display: block;
  font-size: ${fontSizes.tiny};
  text-transform: uppercase;
  letter-spacing: 0.1em;

  & + & {
    margin-top: ${stripUnit(spacing.small) * 1.5 + 'px'};

    ${media.medium`
      margin-top: 0;
      position: relative;

      &:before {
        content: "";
        display: inline-block;
        vertical-align: middle;
        margin: 0 ${spacing.small};
        width: 10px;
        height: 1px;
        background-color: ${white};

        ${props =>
          props.isLight &&
          css`
            background-color: ${black};
          `}

        ${media.medium`
          width: 20px;
        `};

        ${media.large`
          width: 30px;
        `};

        ${media.xLarge`
          width: 35px;
          margin: 0 ${stripUnit(spacing.small) * 1.5 + 'px'};
        `};
      }
    `};
  }
`

const Social = styled.div`
  display: flex;
  flex-direction: column;

  ${media.medium`
    width: 240px;
    padding-left: ${spacing.large};
  `};

  ${media.large`
    width: 290px;
    padding-left: ${spacing.xLarge};
  `};
`

const LogoWrap = styled(Link)`
  display: inline-flex;
  margin-bottom: ${spacing.xLarge};

  ${media.medium`
    align-self: center;
    margin-bottom: 0;
  `};
`

const Footer = ({ isLight }) => (
  <Container isLight={isLight}>
    <Main isLight={isLight}>
      <Section>
        <Wrapper>
          <Inner>
            <LogoWrap to="/" data-tracking="footer--link--image--Vype_Home">
              <DesktopLogo isLight={isLight} className="u-hide-medium-down" />
              <MobileLogo isLight={isLight} className="u-hide-medium-up" />
            </LogoWrap>

            <Links>
              <List>
                <FooterLink isLight={isLight} href="https://batsupport.zendesk.com/hc/en-us" label="Contact Us" />
                <FooterLink isLight={isLight} href="https://batsupport.zendesk.com/hc/en-us" label="FAQs" />
                <FooterLink isLight={isLight} to="/vape-stores" label="Store Locator" />
              </List>
              <Social>
                <SocialLink
                  link="https://www.instagram.com/vusecanada/"
                  label="Instagram"
                  isLight={isLight}
                  data-tracking="footer--link--image--Instagram"
                />
                <SocialLink
                  link="https://www.facebook.com/VuseCanada/"
                  label="Facebook"
                  isLight={isLight}
                  data-tracking="footer--link--image--Facebook"
                />
                <SocialLink
                  link="https://twitter.com/vusecanada"
                  label="Twitter"
                  isLight={isLight}
                  data-tracking="footer--link--image--Twitter"
                />
                <SocialLink
                  link="https://www.youtube.com/channel/UC6SMlvbM74XRYx6u_wEMG0A"
                  label="Youtube"
                  isLight={isLight}
                  data-tracking="footer--link--image--YouTube"
                />
              </Social>
            </Links>
          </Inner>
        </Wrapper>
      </Section>
    </Main>
    <Section>
      <Wrapper>
        <Block>
          <SecondaryList>
            <SecondaryLink to="/pages/conditions-of-sale/" isLight={isLight}>
              Conditions of Sale
            </SecondaryLink>

            <SecondaryLink to="/pages/privacy-policy/" isLight={isLight}>
              Privacy Policy
            </SecondaryLink>

            <SecondaryLink to="/pages/terms-conditions/" isLight={isLight}>
              Terms & Conditions
            </SecondaryLink>

            <SecondaryLink to="/pages/cookie-policy/" isLight={isLight}>
              Cookie Policy
            </SecondaryLink>
          </SecondaryList>
        </Block>

        <Block>
          <Disclaimer isLight={isLight}>
            Vuse products may be harmful to health and contain nicotine which is addictive. VUSE PRODUCTS ARE NOT
            SUITABLE FOR USE BY: persons who are not adults; persons who are under the legal age to purchase vaping
            products; persons who are allergic/sensitive to nicotine; pregnant or breast-feeding women; persons who have
            been advised to avoid using tobacco or nicotine products for medical reasons; persons with reduced physical,
            sensory, mental capabilities or lack of experience/knowledge unless they are under supervision or have been
            given instructions concerning the use of the product by a person responsible for their safety; and persons
            with an unstable heart condition, severe hypertension or diabetes. Keep Vuse products out of reach of
            children.
          </Disclaimer>
          <Disclaimer isLight={isLight}>
            © Imperial Tobacco Company Ltd., 3711 Saint-Antoine Street West, Montreal, QC H4C 3P6
          </Disclaimer>
        </Block>
      </Wrapper>
    </Section>
  </Container>
)

Footer.propTypes = {
  isLight: bool
}

export default Footer
